import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { cn } from '@/utils/cn';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { placeAnchoredMenu, type AnchoredMenuPlacement } from '@/components/ui/placeAnchoredMenu';

const MenuCloseContext = createContext<() => void>(() => undefined);

export function MenuPopover({
  trigger,
  children,
  align = 'end',
  title,
  className,
  defaultOpen = false,
}: {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'end';
  title?: string;
  className?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isDesktop = useIsDesktopNav();
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{
    top: number;
    left: number;
    maxHeight: number;
    placement: AnchoredMenuPlacement;
  } | null>(null);
  const close = () => setOpen(false);

  const focusTrigger = () => {
    wrapRef.current?.querySelector<HTMLElement>('button, [aria-haspopup="menu"]')?.focus();
  };

  useLayoutEffect(() => {
    if (!open || !isDesktop) return;
    const trigger = wrapRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const place = () => {
      const currentMenu = menuRef.current;
      const currentTrigger = wrapRef.current;
      if (!currentMenu || !currentTrigger) return;
      const rect = currentTrigger.getBoundingClientRect();
      setBox(
        placeAnchoredMenu({
          trigger: rect,
          menuWidth: currentMenu.offsetWidth,
          menuHeight: currentMenu.scrollHeight,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          align,
        }),
      );
    };
    place();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(menu);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, isDesktop, align]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent | globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        focusTrigger();
        return;
      }
      if (!isDesktop) return;
      const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
      if (!items || items.length === 0) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') {
        return;
      }
      event.preventDefault();
      const list = Array.from(items);
      const current = list.findIndex((el) => el === document.activeElement);
      let next = 0;
      if (event.key === 'End') next = list.length - 1;
      else if (event.key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % list.length;
      else if (event.key === 'ArrowUp') next = current < 0 ? list.length - 1 : (current - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, isDesktop]);

  type TriggerProps = {
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    'aria-expanded'?: boolean;
    'aria-haspopup'?: 'menu';
  };
  const triggerEl = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<TriggerProps>, {
        onClick: (event: MouseEvent<HTMLElement>) => {
          (trigger as ReactElement<TriggerProps>).props.onClick?.(event);
          setOpen((v) => !v);
        },
        'aria-expanded': open,
        'aria-haspopup': 'menu',
      })
    : trigger;

  const body = <MenuCloseContext.Provider value={close}>{children}</MenuCloseContext.Provider>;

  if (!isDesktop) {
    return (
      <>
        <div ref={wrapRef}>{triggerEl}</div>
        <Dialog open={open} onClose={close} className="relative z-[110]">
          <DialogBackdrop className="fixed inset-0 bg-ds-scrim" />
          <div className="fixed inset-x-0 bottom-0">
            <DialogPanel className="rounded-t-card bg-ds-card px-s4 pb-[calc(16px+env(safe-area-inset-bottom))] pt-s3 shadow-pop">
              <div className="mx-auto mb-s3 h-1 w-10 rounded-full bg-ds-line" aria-hidden />
              {title ? <p className="mb-s2 px-s2 text-[15px] font-semibold text-ds-text">{title}</p> : null}
              <div className="min-w-0">{body}</div>
            </DialogPanel>
          </div>
        </Dialog>
      </>
    );
  }

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-anchored-menu=""
            data-menu-placement={box?.placement ?? 'bottom'}
            style={{
              position: 'fixed',
              top: box?.top ?? 0,
              left: box?.left ?? 0,
              maxHeight: box?.maxHeight,
              visibility: box ? 'visible' : 'hidden',
              zIndex: 110,
            }}
            className={cn(
              'min-w-[240px] overflow-y-auto rounded-pop border border-ds-line bg-ds-card p-s1 shadow-pop',
              className,
            )}
          >
            {body}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={wrapRef} className="relative">
      {triggerEl}
      {isDesktop ? menu : null}
    </div>
  );
}

export function MenuItem({
  icon,
  children,
  description,
  onClick,
  danger,
  kbd,
  active,
  disabled,
  ...rest
}: {
  icon?: ReactNode;
  children: ReactNode;
  description?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  kbd?: string;
  active?: boolean;
  disabled?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children' | 'disabled'>) {
  const close = useContext(MenuCloseContext);
  return (
    <button
      type="button"
      role="menuitem"
      aria-disabled={disabled || undefined}
      {...rest}
      onClick={() => {
        if (disabled) return;
        onClick?.();
        close();
      }}
      className={cn(
        'group flex w-full items-start gap-s3 rounded-lg px-2.5 text-left text-[14px] leading-5',
        description ? 'min-h-[52px] py-2' : 'h-10 items-center',
        danger ? 'text-ds-red-ink' : 'text-ds-text',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : active
            ? 'bg-ds-teal-tint text-ds-teal'
            : 'hover:bg-ds-teal-tint focus-visible:bg-ds-teal-tint',
      )}
      {...rest}
    >
      {icon ? (
        <span
          className={cn(
            'mt-0.5',
            danger
              ? 'text-ds-red-ink'
              : 'text-ds-muted group-hover:text-ds-teal group-focus-visible:text-ds-teal',
            active && 'text-ds-teal',
          )}
        >
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block font-semibold',
            !danger && 'group-hover:text-ds-teal group-focus-visible:text-ds-teal',
            active && 'text-ds-teal',
          )}
        >
          {children}
        </span>
        {description ? (
          <span className="mt-0.5 block text-[12px] font-normal leading-4 text-ds-muted">{description}</span>
        ) : null}
      </span>
      {kbd ? <span className="text-[12px] text-ds-muted">{kbd}</span> : null}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="my-s1 mx-s1 h-px bg-ds-line" role="separator" />;
}
